import type { AnswerRecord, Answer } from "../engine";
import { computePps, formatFastStage } from "../engine/computed";
import { resolve as defaultResolve } from "../i18n/catalogs";
import { interpolate } from "../i18n/interpolate";

export interface CriteriaItem {
  label: string;
  status: "met" | "not_met";
  detail?: string;
}

const EN_FAMILY = { locale: "en", audience: "family" as const };

// Single-arg (key-only) resolver: callers can inject a locale/audience-bound resolver
// (e.g. for recipient-specific PDFs) without threading a ResolveContext through the summary layer.
export type LabelResolver = (key: string) => string;
const defaultLabelResolver: LabelResolver = (key) => defaultResolve(key, EN_FAMILY);

export function formatAnswer(record: AnswerRecord, resolve: LabelResolver = defaultLabelResolver): string {
  const answer: Answer = record.answer;
  const options = record.question.options ?? [];

  if (typeof answer === "boolean") return answer ? resolve("summary.answer.yes") : resolve("summary.answer.no");
  if (answer === "yes") return resolve("summary.answer.yes");
  if (answer === "no") return resolve("summary.answer.no");

  if (Array.isArray(answer)) {
    if (answer.length === 0) return resolve("summary.answer.none_selected");
    return answer
      .map((val) => options.find((o) => o.value === val)?.label ?? val)
      .join(", ");
  }

  if (typeof answer === "string") {
    return options.find((o) => o.value === answer)?.label ?? answer;
  }

  return String(answer);
}

export interface BuildCriteriaStatusOptions {
  computed?: Record<string, number>;
  resolver?: LabelResolver;
}

export function buildCriteriaStatus(
  history: AnswerRecord[],
  opts: BuildCriteriaStatusOptions = {}
): CriteriaItem[] {
  const { computed = {}, resolver: resolve = defaultLabelResolver } = opts;
  const items: CriteriaItem[] = [];
  const categories = history.map((rec) => rec.question.category);

  if (computed.pps !== undefined || categories.includes("pps")) {
    // The PPS still drives the met/not-met status, but the numeric estimate is only shown
    // when the resolved copy carries it: the family estimate resolves to an empty string,
    // so families see a plain functional-status row with no PPS acronym and no percentage.
    const pps = computed.pps !== undefined ? Math.round(computed.pps) : Math.round(computePps(history));
    const estimate = interpolate(resolve("summary.criteria.pps_estimate"), { value: pps });
    items.push({
      label: resolve("summary.criteria.pps_impaired"),
      status: pps < 70 ? "met" : "not_met",
      ...(estimate ? { detail: estimate } : {}),
    });

    const metastases = history.find((rec) => rec.nodeId === "q_distant_metastases");
    if (metastases) {
      items.push({
        label: resolve("summary.criteria.metastases"),
        status: String(metastases.answer) === "yes" ? "met" : "not_met",
      });
    }

    const progression = history.find((rec) => rec.nodeId === "q_disease_progression");
    if (progression) {
      items.push({
        label: resolve("summary.criteria.progression"),
        status: String(progression.answer) === "yes" ? "met" : "not_met",
      });
    }

    const treatment = history.find((rec) => rec.nodeId === "q_treatment_response");
    if (treatment) {
      const val = String(treatment.answer);
      const met = val === "declining_despite_therapy" || val === "declined";
      const opt = treatment.question.options?.find((o) => o.value === val);
      items.push({
        label: resolve("summary.criteria.treatment_decline"),
        status: met ? "met" : "not_met",
        detail: opt?.label,
      });
    }

    const decline = history.find((rec) => rec.nodeId === "q_decline_evidence");
    if (decline && Array.isArray(decline.answer)) {
      const selected = decline.answer as string[];
      const labels = selected.map(
        (v) => decline.question.options?.find((o) => o.value === v)?.label ?? v
      );
      items.push({
        label: resolve("summary.criteria.clinical_decline"),
        status: selected.length > 0 ? "met" : "not_met",
        detail: selected.length > 0 ? labels.join("; ") : undefined,
      });
    }
  } else if (computed.fast !== undefined && !categories.includes("fast")) {
    // As with PPS, the FAST stage still drives the status but the numeric stage is shown
    // only when the resolved copy carries it, so families see no acronym and no number.
    const stage = interpolate(resolve("summary.criteria.fast_stage"), { value: formatFastStage(computed.fast) });
    items.push({
      label: resolve("summary.criteria.fast_stage_label"),
      status: computed.fast >= 7.3 ? "met" : "not_met",
      ...(stage ? { detail: stage } : {}),
    });
  } else if (categories.includes("fast")) {
    const fastCriteria: { nodeId: string; key: string; metAnswer: string }[] = [
      { nodeId: "q_complex_tasks", key: "summary.criteria.fast_complex_tasks", metAnswer: "no" },
      { nodeId: "q_choosing_clothes", key: "summary.criteria.fast_choosing_clothes", metAnswer: "no" },
      { nodeId: "q_dressing", key: "summary.criteria.fast_dressing", metAnswer: "no" },
      { nodeId: "q_bathing", key: "summary.criteria.fast_bathing", metAnswer: "no" },
      { nodeId: "q_toileting", key: "summary.criteria.fast_toileting", metAnswer: "no" },
      { nodeId: "q_bladder_control", key: "summary.criteria.fast_bladder", metAnswer: "no" },
      { nodeId: "q_bowel_control", key: "summary.criteria.fast_bowel", metAnswer: "no" },
    ];

    for (const c of fastCriteria) {
      const rec = history.find((h) => h.nodeId === c.nodeId);
      if (rec) {
        items.push({
          label: resolve(c.key),
          status: String(rec.answer) === c.metAnswer ? "met" : "not_met",
        });
      }
    }

    const speech = history.find((rec) => rec.nodeId === "q_speech");
    if (speech) {
      const val = String(speech.answer);
      const met = val !== "sentences";
      const opt = speech.question.options?.find((o) => o.value === val);
      items.push({ label: resolve("summary.criteria.fast_speech"), status: met ? "met" : "not_met", detail: opt?.label });
    }

    const walking = history.find((rec) => rec.nodeId === "q_walking");
    if (walking) {
      items.push({
        label: resolve("summary.criteria.fast_walking"),
        status: String(walking.answer) === "no" ? "met" : "not_met",
      });
    }

    const sitting = history.find((rec) => rec.nodeId === "q_sitting");
    if (sitting) {
      items.push({
        label: resolve("summary.criteria.fast_sitting"),
        status: String(sitting.answer) === "no" ? "met" : "not_met",
      });
    }

    const complications = history.find((rec) => rec.question.category === "complications");
    if (complications && Array.isArray(complications.answer)) {
      const selected = complications.answer as string[];
      const labels = selected.map(
        (v) => complications.question.options?.find((o) => o.value === v)?.label ?? v
      );
      items.push({
        label: resolve("summary.criteria.fast_complication"),
        status: selected.length > 0 ? "met" : "not_met",
        detail: selected.length > 0 ? labels.join("; ") : undefined,
      });
    }
  }

  return items;
}
